import { t, tf } from "./i18n.js";
// ============================================================
// UI-KIT: Toasts, Bestaetigungs-/Eingabe-Modale, Darstellungsgroesse
//
// Ausgelagert aus ui.js (Refactor 07.08.2026, Schritte 2/3 aus
// REFACTOR_SPLIT.md): Diese Bausteine brauchen auch die eigenstaendigen
// Seiten stats.html und einstellungen.html - ohne den Rest der
// Spiel-Shell zu laden. Der Code selbst ist unveraendert uebernommen.
//
// Erwartet im HTML: #toast-container, #generic-confirm-modal,
// #generic-prompt-modal (Fallbacks sind eingebaut).
// ============================================================

// ==========================================
// TOAST NOTIFICATION SYSTEM
// ==========================================
const TOAST_ICONS = {
  success:
    '<polyline points="20 6 9 17 4 12"></polyline>',
  error:
    '<circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line>',
  info: '<circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line>',
  warning:
    '<path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line>',
};

const TOAST_COLORS = {
  success: "var(--accent-green)",
  error: "var(--accent-red)",
  info: "var(--accent-blue)",
  warning: "var(--accent-orange, #f59e0b)",
};

const TOAST_MAX_VISIBLE = 4;

// type: success | error | info | warning
// duration: Anzeigedauer in ms. Laengere Texte bekommen automatisch mehr Zeit.
function showToast(message, type = "success", duration = null) {
  const container = document.getElementById("toast-container");
  if (!container) return;

  const text = String(message == null ? "" : message);
  const kind = TOAST_ICONS[type] ? type : "info";
  const accentColor = TOAST_COLORS[kind];

  // Aeltere Toasts abraeumen, damit der Stapel nicht den Screen zubaut
  while (container.children.length >= TOAST_MAX_VISIBLE) {
    container.firstElementChild.remove();
  }

  const toast = document.createElement("div");
  toast.className = "toast toast-" + kind;
  toast.setAttribute("role", kind === "error" ? "alert" : "status");
  toast.style.borderLeftColor = accentColor;

  const icon = document.createElement("span");
  icon.className = "toast-icon";
  icon.style.color = accentColor;
  icon.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">${TOAST_ICONS[kind]}</svg>`;

  // textContent statt innerHTML: Spielernamen landen hier ungefiltert.
  const label = document.createElement("span");
  label.className = "toast-text";
  label.textContent = text;

  toast.appendChild(icon);
  toast.appendChild(label);

  // Antippen schliesst sofort
  toast.addEventListener("click", () => dismissToast(toast));

  container.appendChild(toast);
  requestAnimationFrame(() => toast.classList.add("toast-visible"));

  const ms = duration || Math.min(6000, Math.max(2500, text.length * 60));
  setTimeout(() => dismissToast(toast), ms);
}

function dismissToast(toast) {
  if (!toast || toast.dataset.closing) return;
  toast.dataset.closing = "1";
  toast.classList.remove("toast-visible");
  setTimeout(() => toast.remove(), 400);
}

// Sicherheitsnetz: Falls irgendwo doch noch ein alert() steht, landet es als
// Toast statt als blockierender Browser-Dialog.
window.alert = function (message) {
  showToast(message, "info");
};

// ==========================================
// CUSTOM CONFIRM MODAL
// ==========================================
let pendingConfirmAction = null;

function showConfirmModal(message, confirmCallback) {
  // Text anpassen
  document.getElementById("generic-confirm-text").innerText = message;
  // Funktion merken, die beim Klick auf "Ja" ausgeführt werden soll
  pendingConfirmAction = confirmCallback;
  // Modal anzeigen
  document.getElementById("generic-confirm-modal").style.display = "flex";

  // Klick-Event für den roten Button überschreiben
  document.getElementById("generic-confirm-yes-btn").onclick = function () {
    if (pendingConfirmAction) pendingConfirmAction(); // Führt die gemerkte Funktion aus
    closeConfirmModal(); // Schließt das Fenster danach
  };
}

function closeConfirmModal() {
  document.getElementById("generic-confirm-modal").style.display = "none";
  pendingConfirmAction = null;
}

// ==========================================
// EINGABE- UND AUSWAHL-MODAL
// Ersetzt die letzten prompt()-Dialoge. prompt() blockiert den Browser,
// laesst sich nicht gestalten und kennt keine Passwortfelder.
// ==========================================
function closePromptModal() {
  const modal = document.getElementById("generic-prompt-modal");
  if (modal) modal.style.display = "none";
}

// showPromptModal("Titel", "Beschreibung", (wert) => {...}, { value, type, placeholder })
function showPromptModal(title, text, onSubmit, options = {}) {
  const modal = document.getElementById("generic-prompt-modal");
  if (!modal) {
    // Fallback, falls das Modal im HTML fehlt
    const value = window.prompt(text || title, options.value || "");
    if (value !== null) onSubmit(value);
    return;
  }

  const input = document.getElementById("generic-prompt-input");
  const choices = document.getElementById("generic-prompt-choices");
  const okBtn = document.getElementById("generic-prompt-ok");
  const cancelBtn = document.getElementById("generic-prompt-cancel");

  document.getElementById("generic-prompt-title").textContent = title;
  document.getElementById("generic-prompt-text").textContent = text || "";

  choices.innerHTML = "";
  choices.style.display = "none";
  input.style.display = "block";
  input.type = options.type || "text";
  input.value = options.value || "";
  input.placeholder = options.placeholder || "";
  input.maxLength = options.maxLength || 60;
  okBtn.style.display = "block";

  const finish = (value) => {
    closePromptModal();
    if (value !== null) onSubmit(value);
  };

  okBtn.onclick = () => finish(input.value);
  cancelBtn.onclick = () => finish(null);
  input.onkeydown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      finish(input.value);
    }
  };

  modal.style.display = "flex";
  setTimeout(() => input.focus(), 50);
}

// showChoiceModal("Titel", "Text", [{label, value, accent}], (wert) => {...})
function showChoiceModal(title, text, items, onSelect) {
  const modal = document.getElementById("generic-prompt-modal");
  if (!modal) return;

  const input = document.getElementById("generic-prompt-input");
  const choices = document.getElementById("generic-prompt-choices");
  const okBtn = document.getElementById("generic-prompt-ok");
  const cancelBtn = document.getElementById("generic-prompt-cancel");

  document.getElementById("generic-prompt-title").textContent = title;
  document.getElementById("generic-prompt-text").textContent = text || "";

  input.style.display = "none";
  okBtn.style.display = "none";
  choices.style.display = "flex";
  choices.style.flexDirection = "column";
  choices.style.gap = "8px";
  choices.innerHTML = "";

  items.forEach((item) => {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "btn-glass " + (item.accent || "");
    btn.style.margin = "0";
    btn.textContent = item.label;
    btn.onclick = () => {
      closePromptModal();
      onSelect(item.value);
    };
    choices.appendChild(btn);
  });

  cancelBtn.onclick = () => closePromptModal();
  modal.style.display = "flex";
}


// ---------------------------------------------------------------------------
// Darstellungsgroesse
//
// Beim automatischen Zaehlen steht der Spieler an der Abwurflinie, rund
// 1 bis 2 m vom Bildschirm entfernt, und fasst nichts an. Die fluide Skala
// in style.css deckt Fenstergroessen ab; dieser Regler deckt den Abstand ab.
// Beides multipliziert sich, deshalb reicht ein schmaler Bereich von 80 bis
// 160 Prozent.
// ---------------------------------------------------------------------------

const UI_SCALE_KEY = "secure-darts:ui-scale";

function setUiScale(percent) {
  const value = Math.min(Math.max(Number(percent) || 100, 80), 160);
  document.documentElement.style.setProperty("--ui-scale", value / 100);

  const output = document.getElementById("ui-zoom-value");
  if (output) output.textContent = value + " %";
  const slider = document.getElementById("ui-zoom");
  if (slider && Number(slider.value) !== value) slider.value = value;

  try {
    localStorage.setItem(UI_SCALE_KEY, String(value));
  } catch (error) {
    // Privater Modus oder gesperrter Speicher - die Groesse gilt dann nur
    // fuer diese Sitzung. Kein Grund, die Oberflaeche zu stoeren.
  }
}

function loadUiScale() {
  let stored = null;
  try {
    stored = localStorage.getItem(UI_SCALE_KEY);
  } catch (error) {
    stored = null;
  }
  // 90 % als Vorgabe: Die Oberflaeche war bei 100 % auf grossen Monitoren
  // spuerbar zu wuchtig. Wer mehr braucht, stellt hoeher.
  setUiScale(stored === null ? 90 : stored);
}

if (typeof document !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", loadUiScale);
  } else {
    loadUiScale();
  }
}

Object.assign(window, { setUiScale, loadUiScale });

// ---------------------------------------------------------------
// Bruecke zu window: Inline-Handler im HTML und die uebrigen Module
// greifen weiterhin global auf diese Funktionen zu.
// ---------------------------------------------------------------
Object.assign(window, {
  showToast,
  dismissToast,
  showConfirmModal,
  closeConfirmModal,
  closePromptModal,
  showPromptModal,
  showChoiceModal,
});

// ---------------------------------------------------------------------------
// Akzentfarbe (--accent)
//
// Neu im Refactor 07.08.2026 (Schritt 3, REFACTOR_SPLIT.md - "Akzentfarbe
// als CSS-Variable --accent + Auswahl in den Einstellungen"). Die Variable
// haengt an :root (Vorgabe: Blau) und wird hier aus localStorage geladen -
// im selben Muster wie die Darstellungsgroesse darueber.
// ---------------------------------------------------------------------------

const ACCENT_KEY = "secure-darts:accent";

// Nur Namen zulassen, fuer die in style.css eine --accent-*-Variable existiert.
const ACCENT_CHOICES = [
  "green",
  "blue",
  "red",
  "purple",
  "orange",
  "cyan",
  "pink",
  "lime",
];

function setAccentColor(name) {
  // Vorgabe = Logo-Gruen (Einheitsdesign 08.08.2026)
  const value = ACCENT_CHOICES.includes(name) ? name : "green";
  document.documentElement.style.setProperty(
    "--accent",
    `var(--accent-${value})`,
  );

  // Auswahlfeld auf der Einstellungen-Seite synchron halten (Dropdown,
  // Umbau 07.08.2026 Aufgabe 5 - vorher einzelne Farbfelder).
  const select = document.getElementById("accent-select");
  if (select && select.value !== value) select.value = value;

  try {
    localStorage.setItem(ACCENT_KEY, value);
  } catch (error) {
    // Gesperrter Speicher - Farbe gilt dann nur fuer diese Sitzung.
  }
}

function loadAccentColor() {
  let stored = null;
  try {
    stored = localStorage.getItem(ACCENT_KEY);
  } catch (error) {
    stored = null;
  }
  // Auch ohne gespeicherten Wert anwenden: stellt das Dropdown auf die
  // Vorgabe (Logo-Gruen) und macht den Zustand deterministisch.
  setAccentColor(stored || "green");
}

if (typeof document !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", loadAccentColor);
  } else {
    loadAccentColor();
  }
}

Object.assign(window, { setAccentColor, loadAccentColor });

// ---------------------------------------------------------------------------
// Modal-Grundgeruest (GUI-Plan P2.8 + P3.9, 08.08.2026)
//
// Ein gemeinsames Verhalten fuer die Overlay-Dialoge, OHNE ihre bestehende
// Oeffnen/Schliessen-Logik umzubauen: Escape schliesst den obersten
// sichtbaren Dialog, ein Klick auf die dunkle Flaeche ebenso, und Tab
// bleibt im Dialog (Fokus-Falle). Registriert sind nur Dialoge, bei denen
// ein versehentliches Schliessen keinen Schaden anrichtet - laufende
// Spielaktionen (Challenge, Bot-Setup, Checkout-Frage) bewusst nicht.
// ---------------------------------------------------------------------------

const MODAL_REGISTRY = [
  { id: "friend-stats-modal", close: "closeFriendStats" },
  { id: "generic-prompt-modal", close: "closePromptModal" },
  { id: "generic-confirm-modal", close: "closeConfirmModal" },
  { id: "cancel-modal", close: "closeCancelModal" },
  // stats-/compare-Modal haben keine eigene Schliess-Funktion - sie werden
  // direkt ausgeblendet (close: null), exakt wie ihre bisherigen Knoepfe.
  { id: "stats-modal", close: null },
  { id: "compare-modal", close: null },
];

function visibleRegisteredModals() {
  return MODAL_REGISTRY.map((entry) => ({
    entry,
    el: document.getElementById(entry.id),
  })).filter(
    ({ el }) =>
      el && el.style.display !== "none" && el.style.display !== "",
  );
}

function closeTopModal() {
  const open = visibleRegisteredModals();
  if (!open.length) return false;
  const { entry, el } = open[open.length - 1];
  const fn = entry.close ? window[entry.close] : null;
  if (typeof fn === "function") {
    fn();
  } else {
    el.style.display = "none";
  }
  return true;
}

if (typeof document !== "undefined") {
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      if (closeTopModal()) event.preventDefault();
      return;
    }
    // Fokus-Falle: Tab bleibt im obersten registrierten Dialog
    if (event.key === "Tab") {
      const open = visibleRegisteredModals();
      if (!open.length) return;
      const { el } = open[open.length - 1];
      const focusables = el.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!el.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    }
  });

  // Klick auf die dunkle Overlay-Flaeche schliesst den jeweiligen Dialog
  document.addEventListener("click", (event) => {
    const hit = MODAL_REGISTRY.find((entry) => event.target?.id === entry.id);
    if (!hit) return;
    const fn = hit.close ? window[hit.close] : null;
    if (typeof fn === "function") fn();
    else event.target.style.display = "none";
  });
}

// ---------------------------------------------------------------------------
// Leere Zustaende (GUI-Plan P2.5): ein Muster statt nackter Kaesten.
// renderEmptyState(container, { icon, text, actionLabel, onAction })
// ---------------------------------------------------------------------------

function renderEmptyState(container, options = {}) {
  const box = typeof container === "string"
    ? document.getElementById(container)
    : container;
  if (!box) return;

  const wrap = document.createElement("div");
  wrap.className = "empty-state";

  if (options.icon) {
    const icon = document.createElement("div");
    icon.className = "empty-state-icon";
    icon.textContent = options.icon;
    wrap.appendChild(icon);
  }

  const text = document.createElement("p");
  text.className = "empty-state-text";
  text.textContent = options.text || t("js.uikit.001");
  wrap.appendChild(text);

  if (options.actionLabel && typeof options.onAction === "function") {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "btn-glass empty-state-action";
    btn.textContent = options.actionLabel;
    btn.addEventListener("click", options.onAction);
    wrap.appendChild(btn);
  }

  box.innerHTML = "";
  box.appendChild(wrap);
}

// Skeleton-Kacheln (GUI-Plan P3.11) - Platzhalter in KPI-Groesse
function skeletonKpiHtml(count = 6) {
  const tiles = new Array(count).fill('<div class="skeleton-kpi"></div>');
  return `<div class="skeleton-kpi-grid">${tiles.join("")}</div>`;
}

Object.assign(window, { renderEmptyState, skeletonKpiHtml, closeTopModal });
